import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, ListPlus, Loader2, Play, Settings, Sparkles, X } from "lucide-react";
import { StudyNameCell } from "./StudyNameCell";
import ProjectTemplateForm from "./ProjectTemplateForm";
import { PicoExtraction, PicoSubgroup, Document, TemplateDocumentData, FieldValue } from "@/types/models";
import { ExtractedDataCell } from "./ExtractedDataCell";
import { ExcelExportButton } from "./ExcelExportButton";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import { useTemplateDocuments, useDeleteTemplateDocument } from "@/hooks/useProjects";
import { useTemplates, useAllActiveProjectTemplatesWithPrompts } from "@/hooks/useTemplates";
import { useAdminTemplateTypes, useTemplateTypeSettings } from "@/hooks/useMasterData";
import {
  PENDING_SELECTION_STATUS,
  buildEvidenceSelectionDocIds,
  droppedDocIds,
  removalNeedsConfirmation,
  restrictSelectionsToEvidence,
  rowsForDocument,
  scopeDocumentsToSelection,
  withoutPendingSelections,
} from "@/lib/extractionSelection";
import SelectFromEvidenceBaseDialog, { SELECT_FROM_EVIDENCE_BASE_TITLE } from "./evidenceGathering/SelectFromEvidenceBaseDialog";
import TemplateSelectDialog from "./TemplateSelectDialog";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import { usePicoExportRows } from "@/hooks/usePicoExportRows";
import { useProjectDetailContext } from "@/contexts/ProjectDetailContext";
import { Alert, AlertDescription } from "./ui/alert";
import { Card } from "./ui/card";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { AgGridReact } from "ag-grid-react";
import { ColDef, FilterChangedEvent, ICellRendererParams, SelectionChangedEvent } from "ag-grid-community";
import "@/lib/ag-grid-setup";
import ResetFiltersButton from "@/components/ResetFiltersButton";
import { extractionApi, DocumentStatus, isCountedDocStatus } from "@/services/extractionApi";
import { projectsApi } from "@/services/projectsApi";
import { auditLogApi } from "@/services/auditLogApi";
import { useGenerateSubQuestions } from "@/hooks/useContentIntelligenceChat";
import { toSafeFileName } from "@/hooks/useExcelExport";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useExtractionTimer } from "@/hooks/useExtractionTimer";
import TemplateTabStrip from "./TemplateTabStrip";
import type { MentionProvider } from "@lumanity/ui/chat";

interface PicoRow {
  id: string;
  studyName: string;
  authors?: string;
  countries?: string;
  year?: string;
  sourceOrganisation?: string;
  filename?: string;
  studyId?: string;
  status?: string;
  errorMessage?: string;
  fields?: Record<string, string | FieldValue>;
  isParent: boolean;
  isExpanded?: boolean;
  parentId?: string;
  span?: number;
}

interface GridContext {
  toggleExpand: (id: string) => void;
  documents: Document[];
  onViewDocument: (doc: Document, page?: string, sourceText?: string) => void;
  onExtractDocument: (documentId: string) => void;
  extractingDocumentId: string | null;
  isExtracting: boolean;
  documentStatuses: Record<string, DocumentStatus>;
  queueSize: number;
  /** Select from Evidence Base mode: each document row gets an X that removes it from the selection. */
  selectFromEvidenceBase: boolean;
  onRemoveDocument: (documentId: string) => void;
}

const fieldValue = (v: unknown): string =>
  typeof v === "string" ? v : (v as { value?: string })?.value ?? "";

const getStatusString = (status?: string | { value: string; status: string }): string => {
  if (!status) return "";
  if (typeof status === "string") return status;
  return status.value || status.status || "";
};

// Empty grid with Select from Evidence Base on: title, a "Select from Evidence Base" button, then this hint.
export const NO_DOCUMENTS_SELECTED_TITLE = "No documents selected";
export const SELECTION_EMPTY_STATE_HINT =
  "Select publications from your Evidence Base for extraction. Each tab has its own list of publications.";
const GRID_HEADER_HEIGHT = 44;
const EVIDENCE_SELECT_BUSY_REASON = "Wait for the current extraction to complete";
// Step One's key in every workflow (see config/projectTypeConfig.ts).
const EVIDENCE_BASE_STEP_KEY = "Evidence Gathering";

export const formatSelectionLabel = (count: number): string => `${count} selected`;

export const isExtractSelectedSourcesDisabled = (
  isBusyOrUnknown: boolean,
  isExtractingSelectedSources: boolean,
  selectedCount: number
): boolean => isBusyOrUnknown || isExtractingSelectedSources || selectedCount === 0;

export const isSelectableRow = (row: { isParent?: boolean } | null | undefined): boolean => !!row?.isParent;

export const getCheckboxDisabledStyle = (isBusy: boolean): { pointerEvents?: "none"; opacity?: number } =>
  isBusy ? { opacity: 0.5, pointerEvents: "none" } : {};

// A successfully-enqueued batch only clears the selection (every template's) once
// isExtracting — global, since the batch spans every template that had rows checked — turns
// back off.
export const shouldClearSelectionNow = (pendingSelectionClear: boolean, isExtracting: boolean): boolean =>
  pendingSelectionClear && !isExtracting;

// True if an extraction is running, or we haven't yet checked for a resumed one.
export const isExtractionBusyOrUnknown = (isExtracting: boolean, batchResumeChecked: boolean): boolean =>
  isExtracting || !batchResumeChecked;

export const getSelectedDocuments = <TDoc extends { id: string }>(
  documents: TDoc[],
  selectedDocIds: ReadonlySet<string>
): TDoc[] => documents.filter((d) => selectedDocIds.has(d.id));

// Grid checkboxes are scoped per template tab: project template id -> checked document ids.
export type SelectionByTemplate = Record<string, ReadonlySet<string>>;

const EMPTY_SELECTION: ReadonlySet<string> = new Set();

export const setTemplateSelection = (
  selection: SelectionByTemplate,
  templateId: string,
  selectedDocIds: ReadonlySet<string>
): SelectionByTemplate => {
  const next = { ...selection };
  if (selectedDocIds.size > 0) next[templateId] = selectedDocIds;
  else delete next[templateId];
  return next;
};

// Each active template's checked documents, in tab order; templates with nothing checked are skipped.
export const buildTemplateSelections = <TTemplate extends { template: { id: string } }, TDoc extends { id: string }>(
  templates: TTemplate[],
  documents: TDoc[],
  selection: SelectionByTemplate
): { template: TTemplate; docs: TDoc[] }[] =>
  templates
    .map((template) => ({ template, docs: getSelectedDocuments(documents, selection[template.template.id] ?? EMPTY_SELECTION) }))
    .filter(({ docs }) => docs.length > 0);

// Live batch statuses override the derived ones; templates not in the batch keep their derived badge.
export const mergeTemplateStatuses = <TStatus extends string>(
  derived: Record<string, TStatus>,
  batchStatuses: Record<string, TStatus>
): Record<string, TStatus> => ({ ...derived, ...batchStatuses });

// A template's batch total: its selected documents if known, otherwise all documents.
export const getBatchTemplateTotal = (
  trackedDocIds: Record<string, string[]> | null,
  templateId: string,
  allDocumentCount: number
): number => trackedDocIds?.[templateId]?.length ?? allDocumentCount;

// Counts started or finished documents, only among the selected ones when given (others may be Processed from an earlier run).
export const countBatchTemplateProgress = (
  statuses: Record<string, { status: string }>,
  trackedDocIds: string[] | undefined
): number => {
  const relevant = trackedDocIds ? trackedDocIds.map((id) => statuses[id]).filter(Boolean) : Object.values(statuses);
  return relevant.filter((s) => isCountedDocStatus(s.status)).length;
};

// Reads the checked-row ids straight off the ag-grid selection event's row data, so the
// row-data-to-Set logic is testable without an ag-grid SelectionChangedEvent.
export const extractSelectedIds = (rows: Array<{ studyId?: string } | null | undefined>): Set<string> =>
  new Set(rows.map((row) => row?.studyId).filter((id): id is string => !!id));

export const sortTemplateFields = <TField extends { ordinal?: number }>(
  fields: TField[] | undefined,
  missingOrdinalFallback = 0
): TField[] =>
  [...(fields || [])].sort((a, b) => (a.ordinal ?? missingOrdinalFallback) - (b.ordinal ?? missingOrdinalFallback));

// The "Queued" placeholder rows shown immediately on reprocess, before the DB refetch
// replaces them with the real sentinel.
export const buildOptimisticExtractions = (docsToProcess: Document[]): PicoExtraction[] =>
  docsToProcess.map((doc) => ({
    id: `${doc.id}-optimistic`,
    studyId: doc.id,
    studyName: doc.title,
    authors: doc.author,
    countries: [doc.country],
    year: doc.year,
    sourceType: doc.sourceType,
    sourceOrganisation: doc.sourceOrganisation,
    status: "Queued",
    subgroups: [],
  }));


// Drops any existing (stale or optimistic) rows for the documents being reprocessed before
// appending their fresh optimistic rows, so a re-run never leaves duplicate rows for the same
// document.
export const mergeOptimisticExtractions = (
  prev: PicoExtraction[],
  docsToProcess: Document[],
  optimistic: PicoExtraction[]
): PicoExtraction[] => {
  const docIds = new Set(docsToProcess.map((d) => d.id));
  return [...prev.filter((e) => !docIds.has(e.studyId ?? "")), ...optimistic];
};

const isOptimisticRowFor = (docIds: ReadonlySet<string>) => (e: PicoExtraction): boolean =>
  !!e.studyId && docIds.has(e.studyId) && e.id === `${e.studyId}-optimistic`;

// Undoes a failed enqueue's optimistic write: swaps each document's "Queued" placeholder back for
// the rows it replaced, and leaves every other row as it is now. A no-op once the placeholders are
// gone (e.g. switching tabs reset the rows), so another tab's rows are never written back.
export const revertOptimisticExtractions = (
  current: PicoExtraction[],
  replacedRows: PicoExtraction[],
  docIds: ReadonlySet<string>
): PicoExtraction[] => {
  const isOptimistic = isOptimisticRowFor(docIds);
  const revertedDocIds = new Set(current.filter(isOptimistic).map((e) => e.studyId));
  if (revertedDocIds.size === 0) return current;
  return [...current.filter((e) => !isOptimistic(e)), ...replacedRows.filter((e) => revertedDocIds.has(e.studyId))];
};

export const buildEnqueueExtractDocuments = (
  docsToProcess: Document[],
  enrichedColumns: { column_name: string; question: string }[],
  model: string
) =>
  docsToProcess.map((doc) => ({
    document_id: doc.id,
    sourcefile: doc.filename,
    columns: enrichedColumns,
    priming: "",
    model,
  }));

// Single-doc reprocess audits against the document itself; bulk "Extract Selected Sources" audits
// against the template, since there's no single entity the batch belongs to.
export const buildReprocessAuditEvent = (
  projectId: string,
  templateId: string,
  docsToProcess: Document[],
  activeTemplateName: string | undefined,
  auditAction: "reprocess" | "reprocess_selected"
) => {
  const isSingle = auditAction === "reprocess";
  return {
    projectId,
    entityType: (isSingle ? "project_template_document_data" : "project_template"),
    entityId: isSingle ? docsToProcess[0].id : templateId,
    entityName: isSingle ? docsToProcess[0].title : activeTemplateName,
    action: auditAction,
    stage: "Extraction" as const,
    after: JSON.stringify(
      isSingle ? { template_id: templateId } : { template_id: templateId, document_count: docsToProcess.length }
    ),
  };
};


const PicoExtractionView = () => {
  const { projectId, project, projectDocuments: documents, picoExtractions: extractions, setPicoExtractions: onExtractionsChange, activeStage, handleStageComplete, goToStep, continueLabel, pageTitle, templateStatuses, setTemplateStatuses, setStepStateNote, setMentionProvider } = useProjectDetailContext();
  // Admin > Template Types: the grid starts empty and documents come only from the
  // "Select from Evidence Base" popup, saved per template tab as Pending rows.
  const { extraction_select_from_evidence_base } = useTemplateTypeSettings(project?.projectType);
  const selectFromEvidenceBase = extraction_select_from_evidence_base === true;
  const [evidenceSelectOpen, setEvidenceSelectOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const { data: allActiveTemplates = [], isLoading: isTemplateLoading } = useAllActiveProjectTemplatesWithPrompts(projectId, 'Extraction');
  const [selectedProjectTemplateId, setSelectedProjectTemplateId] = useState<string | null>(null);
  const effectiveTemplateId = allActiveTemplates.find(t => t.template.id === selectedProjectTemplateId)?.template.id
    ?? allActiveTemplates[0]?.template.id;
  const activeTemplate = allActiveTemplates.find(t => t.template.id === effectiveTemplateId) ?? null;
  const { data: allTemplates = [] } = useTemplates();
  const { data: allTemplateTypes = [] } = useAdminTemplateTypes();
  const [templateSelectOpen, setTemplateSelectOpen] = useState(false);
  // IMPORTANT: Use template.id (project_template_id), NOT template.source_template_id (master template id)
  const templateId = effectiveTemplateId;
  const { data: templateDocumentRows, isLoading: isDocumentsLoading } = useTemplateDocuments(projectId, templateId);
  // Pending rows are "Select from Evidence Base" picks, not extraction data — everything that
  // reads extraction results or progress uses this filtered view.
  const persistedDocuments = useMemo(() => withoutPendingSelections(templateDocumentRows), [templateDocumentRows]);
  // The viewed tab's selected documents (any active row, Pending included) and the grid's documents.
  // Documents removed with a row's X are hidden at once, before their rows' deletion refetches.
  const [removedSelectionDocIds, setRemovedSelectionDocIds] = useState<ReadonlySet<string>>(new Set());
  const evidenceSelectionDocIds = useMemo(() => {
    const ids = buildEvidenceSelectionDocIds(templateDocumentRows);
    removedSelectionDocIds.forEach((id) => ids.delete(id));
    return ids;
  }, [templateDocumentRows, removedSelectionDocIds]);
  const gridDocuments = useMemo(
    () => scopeDocumentsToSelection(documents ?? [], evidenceSelectionDocIds, selectFromEvidenceBase),
    [documents, evidenceSelectionDocIds, selectFromEvidenceBase]
  );
  const deleteTemplateDocument = useDeleteTemplateDocument();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [localExtractions, setLocalExtractions] = useState<PicoExtraction[]>(extractions);
  const [hasExtracted, setHasExtracted] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractProgress, setExtractProgress] = useState({ current: 0, total: 0 });
  const [extractingDocumentId, setExtractingDocumentId] = useState<string | null>(null);
  const { openDocument } = useDocumentViewer();
  const onViewDocument = (doc: Document, page = "", sourceText?: string) => {
    openDocument({ doc, page, sourceText, side: "left", showTabs: true });
  };
  const [documentStatuses, setDocumentStatuses] = useState<Record<string, DocumentStatus>>({});
  const [queueSize, setQueueSize] = useState<number>(0);
  type TemplateStatus = "queued" | "extracting" | "done" | "error";

  const activeBatchIdRef = useRef<string | null>(null);
  const singleExtractTemplateIdRef = useRef<string | null>(null);
  // True from the moment "Extract All" is clicked until the batch concludes/cancels —
  // lets the banner show immediately, before the server has assigned a real batch id.
  const batchPendingRef = useRef(false);
  // Last known per-document progress for each batch template, so switching to a tab
  // whose progress we've already fetched shows a number instantly instead of re-fetching.
  const progressCacheRef = useRef<Record<string, { current: number; total: number }>>({});
  // De-dupes concurrent fetches for the same template (poll tick vs. a tab switch landing
  // on the same template at the same moment) so they share one network call, not two.
  const inFlightProgressFetchesRef = useRef<Record<string, Promise<{ current: number; total: number }>>>({});
  // Running batch's documents per template (template id -> doc ids); null if unknown, then all documents are assumed.
  const batchTrackedDocIdsRef = useRef<Record<string, string[]> | null>(null);
  // poll()'s closure is created once per startBatchPolling() call and never re-reads
  // `documents` from a fresh render, so a ref keeps it seeing the latest value —
  // matters when a batch is resumed on mount, before `documents` has finished loading.
  const documentsRef = useRef(documents);
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);
  // Single source of truth for "how many documents are there right now" — reads through
  // documentsRef (identity is stable forever, so it's always safe to list as a dependency)
  // instead of each call site recomputing `documentsRef.current?.length ?? 0` separately.
  const getDocumentCount = useCallback((): number => documentsRef.current?.length ?? 0, []);
  const getBatchTotalFor = useCallback(
    (tplId: string): number => getBatchTemplateTotal(batchTrackedDocIdsRef.current, tplId, getDocumentCount()),
    [getDocumentCount]
  );

  // Whether the extraction actually running right now targets the tab being viewed —
  // used instead of raw isExtracting for anything that should stay scoped to one tab.
  const isBatchExtractingActive = isExtracting && (activeBatchIdRef.current !== null || batchPendingRef.current);
  // An "Extract Selected Sources" batch only covers the templates that had rows checked.
  const isInActiveBatch = (tplId: string | null | undefined) =>
    isBatchExtractingActive && !!tplId && tplId in templateStatuses;
  const isSingleExtractTargetTab = (tplId: string | null | undefined) =>
    isExtracting && activeBatchIdRef.current === null && singleExtractTemplateIdRef.current === (tplId ?? null);
  const canCancelExtraction = activeBatchIdRef.current !== null || singleExtractTemplateIdRef.current !== null;

  const templateDocumentQueries = useQueries({
    queries: allActiveTemplates.map(t => ({
      queryKey: ["projects", projectId, "templates", t.template.id, "documents"],
      queryFn: () => projectsApi.getTemplateDocuments(projectId, t.template.id),
      enabled: !!projectId && !!t.template.id && !(isBatchExtractingActive || isSingleExtractTargetTab(t.template.id)),
    })),
  });

  const effectiveStatuses = useMemo<Record<string, TemplateStatus>>(() => {
    const derived: Record<string, TemplateStatus> = {};
    allActiveTemplates.forEach((t, i) => {
      if ((withoutPendingSelections(templateDocumentQueries[i]?.data)?.length ?? 0) > 0) {
        derived[t.template.id] = "done";
      }
    });
    return mergeTemplateStatuses(derived, templateStatuses as Record<string, TemplateStatus>);
  }, [templateStatuses, allActiveTemplates, templateDocumentQueries]);

  const prevTemplateIdRef = useRef<string | null>(null);
  const cancelRequestedRef = useRef(false);

  const extractionTimer = useExtractionTimer();

  const generateSubQs = useGenerateSubQuestions();
  const queryClient = useQueryClient();

  const invalidateTemplateDocuments = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["projects", projectId, "templates", templateId, "documents"],
    });
  }, [queryClient, projectId, templateId]);

  const resetExtractionState = useCallback(() => {
    setIsExtracting(false);
    setDocumentStatuses({});
    setQueueSize(0);
    singleExtractTemplateIdRef.current = null;
    batchPendingRef.current = false;
    batchTrackedDocIdsRef.current = null;
    progressCacheRef.current = {};
    extractionTimer.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- extractionTimer.reset is stable; the wrapping object is not
  }, [extractionTimer.reset]);

  // Fetches (and caches) per-document progress for one batch template. Concurrent callers
  // for the same template share the same in-flight request instead of firing twice.
  const getBatchTemplateProgress = useCallback((
    tplId: string
  ): Promise<{ current: number; total: number }> => {
    const total = getBatchTotalFor(tplId);
    const existing = inFlightProgressFetchesRef.current[tplId];
    if (existing) return existing;

    const promise = (async () => {
      try {
        const { statuses } = await extractionApi.getExtractionStatus(projectId, tplId);
        const current = countBatchTemplateProgress(statuses, batchTrackedDocIdsRef.current?.[tplId]);
        const result = { current, total };
        progressCacheRef.current[tplId] = result;
        return result;
      } catch (err) {
        console.error("Failed to fetch per-document status for batch progress:", err);
        return progressCacheRef.current[tplId] ?? { current: 0, total };
      } finally {
        delete inFlightProgressFetchesRef.current[tplId];
      }
    })();
    inFlightProgressFetchesRef.current[tplId] = promise;
    return promise;
  }, [projectId, getBatchTotalFor]);

  // Recompute the displayed progress immediately when the active tab changes mid-batch,
  // instead of waiting for the next poll tick. Uses the cached value for a template that's
  // currently extracting when we already have one, so switching tabs shows a number instantly.
  useEffect(() => {
    if (!isExtracting || !activeBatchIdRef.current || !effectiveTemplateId) return;
    const total = getBatchTotalFor(effectiveTemplateId);
    const status = templateStatuses[effectiveTemplateId];

    if (status === "done" || status === "error") {
      setExtractProgress({ current: total, total });
      return;
    }
    if (status === "extracting") {
      const cached = progressCacheRef.current[effectiveTemplateId];
      if (cached) {
        setExtractProgress(cached);
      } else {
        getBatchTemplateProgress(effectiveTemplateId).then(setExtractProgress);
      }
      return;
    }
    // "queued" or unknown — this template hasn't started yet
    setExtractProgress({ current: 0, total });
  }, [effectiveTemplateId, isExtracting, templateStatuses, getBatchTotalFor, getBatchTemplateProgress]);

  const startBatchPolling = useCallback(() => {
    if (pollingIntervalRef.current !== null) {
      globalThis.clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    let consecutiveErrors = 0;

    const poll = async () => {
      try {
        const { template_statuses, all_done } = await projectsApi.getExtractionBatchStatus(projectId);
        // The user may have clicked Cancel while this request was in flight — don't act on
        // stale data or reschedule another poll once that's happened.
        if (cancelRequestedRef.current) return;
        consecutiveErrors = 0;

        setTemplateStatuses(template_statuses as Record<string, TemplateStatus>);

        const extractingId = Object.entries(template_statuses).find(([, s]) => s === "extracting")?.[0];
        let extractingProgress: { current: number; total: number } | null = null;
        if (extractingId) {
          queryClient.invalidateQueries({ queryKey: ["projects", projectId, "templates", extractingId, "documents"] });
          // Refresh the cached per-document count for whichever template is extracting, so a
          // tab switch to it shows an up-to-date number instantly instead of waiting on a fetch.
          extractingProgress = await getBatchTemplateProgress(extractingId);
          if (prevTemplateIdRef.current === extractingId) {
            setExtractProgress(extractingProgress);
          }
        }

        const allTerminal =
          Object.keys(template_statuses).length > 0 &&
          Object.values(template_statuses).every(s => s === "done" || s === "error");

        // Feed the shared (single-instance) timer whole-batch progress, not the per-tab-viewed
        // template — otherwise switching tabs mid-batch looks like `current` regressing and
        // corrupts the hook's rolling rate calculation. Each template contributes its own
        // total, since an "Extract Selected Sources" batch can cover a different subset per template.
        const globalTotal = Object.keys(template_statuses).reduce((sum, tplId) => sum + getBatchTotalFor(tplId), 0);
        if (globalTotal > 0) {
          const globalCurrent = Object.entries(template_statuses).reduce((sum, [tplId, status]) => {
            if (status === "done" || status === "error") return sum + getBatchTotalFor(tplId);
            if (status === "extracting" && tplId === extractingId) return sum + (extractingProgress?.current ?? 0);
            return sum;
          }, 0);
          // The still-"extracting" template's per-doc count can reach its full quota (all docs
          // Processed) a poll tick before its status here flips to done/error, which would make
          // globalCurrent hit globalTotal early and trip update()'s implicit completion-clear
          // before the batch has actually finished. Clamp until status confirms it's terminal.
          const safeGlobalCurrent = all_done || allTerminal ? globalCurrent : Math.min(globalCurrent, globalTotal - 1);
          extractionTimer.update(safeGlobalCurrent, globalTotal);
        }

        if (all_done || allTerminal) {
          for (const tplId of Object.keys(template_statuses)) {
            queryClient.invalidateQueries({ queryKey: ["projects", projectId, "templates", tplId, "documents"] });
          }
          activeBatchIdRef.current = null;
          batchPendingRef.current = false;
          batchTrackedDocIdsRef.current = null;
          setIsExtracting(false);
          setHasExtracted(true);
        } else {
          pollingIntervalRef.current = globalThis.setTimeout(poll, 10000) as unknown as number;
        }
      } catch (err) {
        if (cancelRequestedRef.current) return;
        consecutiveErrors += 1;
        console.error(`Batch status polling error (attempt ${consecutiveErrors}):`, err);
        // Never give up tracking on our own — a transient blip shouldn't hide the estimate
        // before the backend actually reports completion. Back off instead of retrying blind.
        const delay = Math.min(10000 * 2 ** consecutiveErrors, 60000);
        pollingIntervalRef.current = globalThis.setTimeout(poll, delay) as unknown as number;
      }
    };

    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- extractionTimer.update is stable; the wrapping object is not
  }, [projectId, queryClient, setTemplateStatuses, getBatchTemplateProgress, getBatchTotalFor, extractionTimer.update]);

  const buildEnrichedColumns = useCallback(async (baseColumns: { column_name: string; question: string }[]) => {
    const subQuestionsPerColumn = await Promise.all(
      baseColumns.map((col) => generateSubQs(col.question, project.model))
    );
    return baseColumns.map((col, i) => {
      const questions = subQuestionsPerColumn[i];
      return questions.length > 0 ? { ...col, questions } : col;
    });
  }, [generateSubQs, project.model]);

  const pollingIntervalRef = useRef<number | null>(null);
  // False once the view unmounts, so async work that resolves afterwards doesn't start polling.
  const isMountedRef = useRef(true);
  const hasResumedPollingRef = useRef(false);
  // Gates the single-template resume effect until the batch-resume check below has
  // definitively finished (found a batch or not), so the two "resume on mount" checks
  // can't both decide to start polling for the same project at once.
  const [batchResumeChecked, setBatchResumeChecked] = useState(false);
  const isBusyOrUnknown = isExtractionBusyOrUnknown(isExtracting, batchResumeChecked);
  const [isExtractingSelectedSources, setIsExtractingSelectedSources] = useState(false);

  const refuseIfBusy = useCallback((callerName: string): boolean => {
    // isExtractingSelectedSources covers the window between clicking Extract Selected Sources and isExtracting
    // actually flipping true (enrichment + enqueue are awaited before startPolling sets it) —
    // without it, a single-document Reprocess click in that window would race the batch.
    if (!isBusyOrUnknown && !isExtractingSelectedSources) return false;
    console.warn(`${callerName}: another extraction is already in progress or still being checked`);
    return true;
  }, [isBusyOrUnknown, isExtractingSelectedSources]);

  const gridRef = useRef<AgGridReact<PicoRow>>(null);
  const autoOpenedSheetRef = useRef(false);
  const processedFetchedRef = useRef<Set<string>>(new Set());
  const [hasGridColumnFilters, setHasGridColumnFilters] = useState(false);
  const [selectedDocIdsByTemplate, setSelectedDocIdsByTemplate] = useState<SelectionByTemplate>({});
  const [pendingSelectionClear, setPendingSelectionClear] = useState(false);
  const viewedTabSelectedIds = (effectiveTemplateId && selectedDocIdsByTemplate[effectiveTemplateId]) || EMPTY_SELECTION;
  const templateDocumentRowsById = useMemo(
    () => Object.fromEntries(allActiveTemplates.map((t, i) => [t.template.id, templateDocumentQueries[i]?.data])),
    [allActiveTemplates, templateDocumentQueries]
  );
  const templateSelections = useMemo(() => {
    const selections = buildTemplateSelections(allActiveTemplates, documents ?? [], selectedDocIdsByTemplate);
    // A document removed via Select from Evidence Base may still be ticked — never extract it.
    return selectFromEvidenceBase ? restrictSelectionsToEvidence(selections, templateDocumentRowsById) : selections;
  }, [allActiveTemplates, documents, selectedDocIdsByTemplate, selectFromEvidenceBase, templateDocumentRowsById]);
  const totalSelectedCount = templateSelections.reduce((sum, s) => sum + s.docs.length, 0);

  // The grid is keyed by template (see <AgGridReact key=...>), so every tab gets its own grid
  // instance and this only ever sees selection changes for the tab being viewed.
  const onGridSelectionChanged = useCallback((event: SelectionChangedEvent<PicoRow>) => {
    if (!effectiveTemplateId) return;
    const ids = extractSelectedIds(event.api.getSelectedNodes().map((node) => node.data));
    setSelectedDocIdsByTemplate((prev) => setTemplateSelection(prev, effectiveTemplateId, ids));
  }, [effectiveTemplateId]);

  const handleClearSelection = useCallback(() => {
    gridRef.current?.api?.deselectAll();
    setSelectedDocIdsByTemplate({});
  }, []);

  const selectionColumnDef = useMemo(() => {
    const disabledCheckboxCSS = getCheckboxDisabledStyle(isBusyOrUnknown || isExtractingSelectedSources);
    return {
      headerStyle: { borderLeft: "4px solid transparent", ...disabledCheckboxCSS },
      cellStyle: (params: { data?: { isParent?: boolean } }) => ({
        borderLeft: `4px solid ${params.data?.isParent ? "hsl(var(--brand-teal))" : "transparent"}`,
        ...disabledCheckboxCSS,
      }),
    };
  }, [isBusyOrUnknown, isExtractingSelectedSources]);

  const rowSelection = useMemo(() => ({
    mode: "multiRow" as const,
    checkboxes: (params: { data?: { isParent?: boolean } }) => isSelectableRow(params.data),
    hideDisabledCheckboxes: true,
    isRowSelectable: (node: { data?: { isParent?: boolean } }) => isSelectableRow(node.data),
  }), []);

  const handleResetGridFilters = useCallback(() => {
    gridRef.current?.api?.setFilterModel(null);
    setHasGridColumnFilters(false);
  }, []);

  const onFilterChanged = useCallback((event: FilterChangedEvent) => {
    const filterModel = event.api.getFilterModel();
    setHasGridColumnFilters(Object.keys(filterModel).length > 0);
  }, []);

  // Auto-open template sheet on load when an extraction template is assigned but no extractions exist yet
  useEffect(() => {
    if (autoOpenedSheetRef.current) return;
    if (isTemplateLoading || isDocumentsLoading) return;
    if (activeTemplate && persistedDocuments?.length === 0) {
      autoOpenedSheetRef.current = true;
      setTemplateOpen(true);
    }
  }, [isTemplateLoading, isDocumentsLoading, activeTemplate, persistedDocuments]);

  // On mount, resume any batch extraction that was running before the component unmounted
  useEffect(() => {
    if (!projectId) return;
    if (activeBatchIdRef.current !== null) return; // Extract All already started this session
    let cancelled = false;

    projectsApi.getExtractionBatchStatus(projectId)
      .then(({ batch_id, template_statuses, template_document_ids, all_done }) => {
        if (cancelled) return;
        if (activeBatchIdRef.current !== null) return; // Extract All already started this session
        if (!all_done && Object.keys(template_statuses).length > 0) {
          // Refetch every batch template's documents rather than trusting a possibly stale
          // cache from before the batch. Not cleared outright: an "Extract Selected Sources" batch only
          // reprocesses some documents, and the rest must stay visible in the grid.
          // We use template_statuses keys (template IDs) directly — allActiveTemplates may not
          // be loaded yet at this point in the mount lifecycle.
          autoOpenedSheetRef.current = true; // prevent auto-open-on-empty from firing
          Object.keys(template_statuses).forEach(tplId => {
            queryClient.invalidateQueries({ queryKey: ["projects", projectId, "templates", tplId, "documents"] });
          });
          activeBatchIdRef.current = batch_id;
          batchTrackedDocIdsRef.current = template_document_ids ?? null;
          hasResumedPollingRef.current = true; // block single-doc polling resume
          setIsExtracting(true);
          const templateIds = Object.keys(template_statuses);
          extractionTimer.start(templateIds.reduce((sum, tplId) => sum + getBatchTotalFor(tplId), 0));
          setTemplateStatuses(template_statuses as Record<string, TemplateStatus>);
          setExtractProgress({ current: 0, total: getBatchTotalFor(templateIds[0]) });
          startBatchPolling();
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setBatchResumeChecked(true);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- extractionTimer.start is stable; the wrapping object is not
  }, [projectId, queryClient, startBatchPolling, setTemplateStatuses, getBatchTotalFor]);

  // Reset per-template view state when the active template changes
  useEffect(() => {
    const prev = prevTemplateIdRef.current;
    prevTemplateIdRef.current = effectiveTemplateId ?? null;
    if (!prev || prev === effectiveTemplateId) return;
    setLocalExtractions([]);
    setExpandedIds(new Set());
    setHasExtracted(false);
    setDocumentStatuses({});
    setQueueSize(0);
    setHasGridColumnFilters(false); // the grid is keyed by template, so the new tab's grid starts unfiltered
    setRemovedSelectionDocIds(new Set());
    setRemoveSelectionError(null);
  }, [effectiveTemplateId]);

  // Clean up polling interval on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollingIntervalRef.current !== null) {
        globalThis.clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const startPolling = useCallback(async (trackedDocIds: string[]) => {
    if (pollingIntervalRef.current !== null) {
      globalThis.clearInterval(pollingIntervalRef.current);
    }

    const total = trackedDocIds.length;
    setIsExtracting(true);
    singleExtractTemplateIdRef.current = templateId ?? null;

    const stopPolling = (done: boolean) => {
      globalThis.clearInterval(pollingIntervalRef.current!);
      pollingIntervalRef.current = null;
      resetExtractionState();
      if (done) setHasExtracted(true);
      invalidateTemplateDocuments();
    };

    const pollStatus = async () => {
      try {
        const { statuses, all_done, queue_size } = await extractionApi.getExtractionStatus(projectId, templateId!);
        // The user may have clicked Cancel while this request was in flight — don't act on
        // stale data once that's happened.
        if (cancelRequestedRef.current) return;

        // When tracking specific docs (single reprocess), count only those; otherwise count all.
        const tracked = trackedDocIds.length > 0
          ? Object.fromEntries(Object.entries(statuses).filter(([id]) => trackedDocIds.includes(id)))
          : statuses;

        const doneCount = Object.values(tracked).filter((s) => isCountedDocStatus(s.status)).length;
        setExtractProgress({ current: doneCount, total });

        const allDocsDone = total > 0 && Object.values(tracked).every(
          s => s.status === "Processed" || s.status === "Error"
        );

        // `doneCount` treats "Processing" as counted (same as the progress display above), so
        // it can reach `total` a poll tick before every doc has actually finished. Clamp what
        // feeds the timer so its own completion check doesn't clear the estimate early.
        const safeDoneCount = all_done || allDocsDone ? doneCount : Math.min(doneCount, total - 1);
        extractionTimer.update(safeDoneCount, total);

        // Update document statuses and queue size
        setDocumentStatuses(statuses);
        setQueueSize(queue_size);

        // Update localExtractions with latest statuses from polling
        setLocalExtractions((prev) => {
          return prev.map((extraction) => {
            const docStatus = statuses[extraction.studyId];
            if (docStatus && docStatus.status !== extraction.status) {
              return { ...extraction, status: docStatus.status };
            }
            return extraction;
          });
        });

        // Invalidate as soon as a doc turns Processed so its rows appear without waiting for all_done.
        const newlyProcessed = Object.entries(statuses).filter(
          ([docId, s]) => s.status === "Processed" && !processedFetchedRef.current.has(docId)
        );
        if (newlyProcessed.length > 0) {
          newlyProcessed.forEach(([docId]) => processedFetchedRef.current.add(docId));
          invalidateTemplateDocuments();
        }

        // If any document has Error status, reload from DB to surface the error message in the tooltip.
        // The polling API doesn't return error details, so we rely on the DB record.
        if (Object.values(statuses).some((s) => s.status === "Error")) {
          invalidateTemplateDocuments();
        }

        if (all_done || allDocsDone) {
          stopPolling(true);
        }
      } catch (err) {
        console.error("Extraction status polling error:", err);
      }
    };

    // Fetch immediately on start
    await pollStatus();

    // Then poll every 10 seconds
    pollingIntervalRef.current = globalThis.setInterval(pollStatus, 10000) as unknown as number;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- extractionTimer.update is stable; the wrapping object is not
  }, [projectId, templateId, invalidateTemplateDocuments, resetExtractionState, extractionTimer.update]);

  // Load persisted extractions on component mount
  useEffect(() => {
    if (!persistedDocuments || !documents) return;

    // Group records by document_id
    const groupedByDocument = new Map<string, TemplateDocumentData[]>();
    persistedDocuments.forEach((templateDoc) => {
      if (!groupedByDocument.has(templateDoc.documentId)) {
        groupedByDocument.set(templateDoc.documentId, []);
      }
      groupedByDocument.get(templateDoc.documentId)!.push(templateDoc);
    });

    // Reconstruct PicoExtraction for each document
    const loadedExtractions: PicoExtraction[] = [];
    groupedByDocument.forEach((records, documentId) => {
      const document = documents.find((d) => d.id === documentId);
      if (!document) {
        console.warn(`Document ${documentId} not found for persisted extraction`);
        return;
      }

      // Get status from first record (all should have same status per document)
      const status = records[0]?.status || "Processed";
      const createdAt = records[0]?.createdAt || new Date().toISOString();

      // Check if these are sentinel records (Queued/Processing with no data)
      const isSentinel = status === "Queued" || status === "Processing";

      // Extract error message from error sentinel records
      let errorMessage: string | undefined;
      if (status === "Error") {
        for (const record of records) {
          if (!record.data) continue;
          try {
            const parsed = JSON.parse(record.data);
            if (parsed.error !== undefined && parsed.error !== null) {
              errorMessage = String(parsed.error);
              break;
            }
          } catch { /* ignore */ }
        }
      }

      let subgroups: PicoSubgroup[] = [];

      if (!isSentinel) {
        // Parse actual extraction data
        const subgroupsWithIndex = records
          .map((record) => {
            if (!record.data) return null;
            try {
              const parsed = JSON.parse(record.data);
              if (!parsed.fields && parsed.error !== undefined) return null;
              const rawFields: Record<string, string | FieldValue> = parsed.fields ?? {};
              const fields: Record<string, FieldValue> = Object.fromEntries(
                Object.entries(rawFields).map(([k, v]) => [
                  k,
                  typeof v === "string" ? { value: v, status: "pending" } : v,
                ])
              );
              return {
                index: parsed.index ?? 0,
                subgroup: {
                  population: parsed.population,
                  intervention: parsed.intervention,
                  comparator: parsed.comparator,
                  outcome: parsed.outcome,
                  fields,
                } as PicoSubgroup,
              };
            } catch (err) {
              console.error(`Failed to parse subgroup data for document ${documentId}:`, err);
              return null;
            }
          })
          .filter((item): item is { index: number; subgroup: PicoSubgroup } => item !== null);

        // Sort by index and extract subgroups
        subgroupsWithIndex.sort((a, b) => a.index - b.index);
        subgroups = subgroupsWithIndex.map((item) => item.subgroup);
      }

      const extraction: PicoExtraction = {
        id: `${document.id}-${createdAt}`,
        studyName: document.title,
        authors: document.author,
        countries: [document.country],
        year: document.year,
        sourceType: document.sourceType,
        sourceOrganisation: document.sourceOrganisation,
        studyId: document.id,
        status,
        subgroups,
        errorMessage,
      };

      loadedExtractions.push(extraction);
    });

    if (loadedExtractions.length > 0) {
      // DB data always wins — replace any in-memory entry for documents returned by the DB,
      // keep in-memory entries for documents the DB didn't return (e.g. mid-poll updates).
      setLocalExtractions((prev) => {
        const loadedStudyIds = new Set(loadedExtractions.map((e) => e.studyId));
        const unaffected = prev.filter((e) => !loadedStudyIds.has(e.studyId));
        return [...unaffected, ...loadedExtractions];
      });

      // Expand only studies that have actual child rows
      setExpandedIds((prev) => {
        const updated = new Set(prev);
        loadedExtractions.filter((e) => e.subgroups.length > 0).forEach((e) => updated.add(e.studyId));
        return updated;
      });

      setHasExtracted(true);
    }
  }, [persistedDocuments, documents]);

  // Auto-resume polling if extraction was in progress when the component last unmounted
  useEffect(() => {
    // Wait for the batch-resume check (above) to finish first — otherwise both effects could
    // decide to start polling for the same project at once, feeding conflicting totals into
    // the one shared timer instance.
    if (!batchResumeChecked) return;
    if (!persistedDocuments || !documents || !templateId || hasResumedPollingRef.current) return;

    const hasInProgress = persistedDocuments.some(
      (d) => d.status === "Queued" || d.status === "Processing"
    );
    if (!hasInProgress) return;

    hasResumedPollingRef.current = true;

    // Seed the progress counter from persisted data before the first poll fires
    const doneDocIds = new Set(
      persistedDocuments
        .filter((d) => isCountedDocStatus(d.status))
        .map((d) => d.documentId)
    );
    // Only the grid's documents: with Select from Evidence Base on, that's the tab's selection.
    setExtractProgress({ current: doneDocIds.size, total: gridDocuments.length });
    cancelRequestedRef.current = false;
    extractionTimer.start(gridDocuments.length);

    startPolling(gridDocuments.map((d) => d.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- extractionTimer.start is stable; the wrapping object is not
  }, [persistedDocuments, documents, gridDocuments, templateId, startPolling, batchResumeChecked]);

  // Optimistically shows "Queued" spinners for the given documents — DB refetch replaces these with
  // real sentinels. Returns an undo for a failed enqueue, which restores just these documents' rows
  // (the refetch alone won't re-render them if server data is unchanged).
  const showOptimisticRows = useCallback((docs: Document[]): (() => void) => {
    const docIds = new Set(docs.map((d) => d.id));
    // Filled in by the updater below, which always runs before the undo's updater does.
    let replacedRows: PicoExtraction[] = [];
    docs.forEach((doc) => processedFetchedRef.current.delete(doc.id));
    setLocalExtractions((prev) => {
      replacedRows = prev.filter((e) => docIds.has(e.studyId ?? ""));
      return mergeOptimisticExtractions(prev, docs, buildOptimisticExtractions(docs));
    });
    return () => setLocalExtractions((prev) => revertOptimisticExtractions(prev, replacedRows, docIds));
  }, []);

  // Single-document "Reprocess" on the viewed tab: queues the given documents, enqueues
  // extraction, and starts polling. (Bulk "Extract Selected Sources" spans templates, so it goes through
  // enqueueSelectedBatch instead.) Callers' buttons already gate on isExtractionBusyOrUnknown;
  // the check below is just a safety net.
  const enqueueReprocessForDocuments = useCallback(async (docsToProcess: Document[]): Promise<boolean> => {
    if (refuseIfBusy("enqueueReprocessForDocuments")) return false;

    if (!activeTemplate || docsToProcess.length === 0) {
      console.warn("enqueueReprocessForDocuments: no active template or no documents to process", {
        hasActiveTemplate: !!activeTemplate,
        docCount: docsToProcess.length,
      });
      return false;
    }

    const sortedFields = sortTemplateFields(activeTemplate.prompt_libraries);
    if (sortedFields.length === 0) {
      console.warn("enqueueReprocessForDocuments: active template has no prompt library fields, nothing to extract");
      return false;
    }

    const undoOptimisticRows = showOptimisticRows(docsToProcess);

    try {
      // Build base columns and enrich with AI-generated sub-questions
      const baseColumns = sortedFields.map((f) => ({
        column_name: f.name,
        question: f.priming || "",
      }));
      const enrichedColumns = await buildEnrichedColumns(baseColumns);
      await extractionApi.enqueueExtract(projectId, {
        template_id: templateId,
        documents: buildEnqueueExtractDocuments(docsToProcess, enrichedColumns, project.model),
      });
    } catch (err) {
      console.error("Failed to enqueue reprocess for documents:", docsToProcess.map((d) => d.id), err);
      undoOptimisticRows();
      invalidateTemplateDocuments();
      return false;
    }

    void auditLogApi.recordAuditEvent(
      buildReprocessAuditEvent(projectId, templateId, docsToProcess, activeTemplate?.template.name, "reprocess")
    );

    // The guard above already confirmed nothing else is running, so this is always our own
    // poll to start. No invalidate here — could race the enqueue write and read back a stale
    // status; startPolling's first pollStatus() tick invalidates once it confirms the real one.
    cancelRequestedRef.current = false;
    extractionTimer.start(docsToProcess.length);
    startPolling(docsToProcess.map((d) => d.id));
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- extractionTimer.start is stable; the wrapping object is not
  }, [refuseIfBusy, activeTemplate, showOptimisticRows, buildEnrichedColumns, projectId, templateId, project.model, startPolling, invalidateTemplateDocuments]);

  // Extract data for a single document
  const handleExtractSingleDocument = async (documentId: string) => {
    if (!documents || !activeTemplate) return;

    const doc = documents.find((d) => d.id === documentId);
    if (!doc) {
      console.error(`Document with id ${documentId} not found`);
      return;
    }

    await enqueueReprocessForDocuments([doc]);
  };

  // Sends every template's checked documents as one sequential extraction batch — the server
  // runs the templates one after another, each reprocessing only its own selection — and starts
  // batch polling.
  const enqueueSelectedBatch = useCallback(async (
    selections: typeof templateSelections
  ): Promise<boolean> => {
    // Only the viewed tab's grid is on screen, so only its documents get "Queued" spinners
    const viewed = selections.find((s) => s.template.template.id === templateId);
    const undoOptimisticRows = viewed ? showOptimisticRows(viewed.docs) : () => {};

    let batchId: string;
    let batchStatuses: Record<string, string>;
    try {
      const batches = await Promise.all(selections.map(async ({ template, docs }) => {
        const baseColumns = sortTemplateFields(template.prompt_libraries).map((f) => ({
          column_name: f.name,
          question: f.priming || "",
        }));
        const enrichedColumns = await buildEnrichedColumns(baseColumns);
        return {
          template_id: template.template.id,
          documents: buildEnqueueExtractDocuments(docs, enrichedColumns, project.model),
        };
      }));
      ({ batch_id: batchId, template_statuses: batchStatuses } = await projectsApi.createExtractionBatch(projectId, batches));
    } catch (err) {
      console.error("Failed to start Extract Selected Sources batch:", err);
      undoOptimisticRows();
      invalidateTemplateDocuments();
      return false;
    }

    selections.forEach(({ template, docs }) => {
      void auditLogApi.recordAuditEvent(
        buildReprocessAuditEvent(projectId, template.template.id, docs, template.template.name, "reprocess_selected")
      );
    });

    // Navigated away while the batch was being created: it still runs server-side, and the
    // batch-resume-on-mount effect picks it up on return — don't start polling from a dead view.
    if (!isMountedRef.current) return false;

    const tracked = Object.fromEntries(selections.map(({ template, docs }) => [template.template.id, docs.map((d) => d.id)]));
    activeBatchIdRef.current = batchId;
    batchTrackedDocIdsRef.current = tracked;
    progressCacheRef.current = {};
    cancelRequestedRef.current = false;
    hasResumedPollingRef.current = true; // the batch's sentinels mustn't also trigger single-template polling resume
    setTemplateStatuses(batchStatuses as Record<string, TemplateStatus>);
    setIsExtracting(true);
    extractionTimer.start(selections.reduce((sum, s) => sum + s.docs.length, 0));
    startBatchPolling();
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- extractionTimer.start is stable; the wrapping object is not
  }, [templateId, showOptimisticRows, buildEnrichedColumns, project.model, projectId, invalidateTemplateDocuments, setTemplateStatuses, startBatchPolling]);

  // Extract data for whichever documents are checked, across every template tab
  const handleExtractSelectedSources = useCallback(async () => {
    if (!documents || totalSelectedCount === 0 || isExtractingSelectedSources) return;
    if (refuseIfBusy("handleExtractSelectedSources")) return;

    const selections = templateSelections.filter(({ template }) => {
      const hasFields = (template.prompt_libraries?.length ?? 0) > 0;
      if (!hasFields) console.warn(`handleExtractSelectedSources: template ${template.template.id} has no prompt library fields, skipping it`);
      return hasFields;
    });
    if (selections.length === 0) return;

    setIsExtractingSelectedSources(true);
    try {
      const succeeded = await enqueueSelectedBatch(selections);
      if (succeeded) setPendingSelectionClear(true);
    } finally {
      setIsExtractingSelectedSources(false);
    }
  }, [documents, totalSelectedCount, isExtractingSelectedSources, refuseIfBusy, templateSelections, enqueueSelectedBatch]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const templateColumns = useMemo(() => {
    if (!activeTemplate?.prompt_libraries?.length) return [];
    return sortTemplateFields(activeTemplate.prompt_libraries, Infinity);
  }, [activeTemplate]);

  const populationColName = useMemo(() => {
    const fallback = templateColumns[0]?.name ?? "Population";
    if (!activeTemplate) return fallback;
    const masterTemplate = allTemplates.find(
      (t) => t.id === activeTemplate.template.source_template_id
    );
    if (!masterTemplate?.template_type_id) return fallback;
    const templateType = allTemplateTypes.find(
      (t) => t.id === masterTemplate.template_type_id
    );
    if (!templateType?.settings) return fallback;
    try {
      const parsed = JSON.parse(templateType.settings);
      const rules: Array<{ rule: string; value: string }> = Array.isArray(parsed?.validation)
        ? parsed.validation
        : [];
      const rule = rules.find((r) => r.rule === "first_column_name");
      return rule?.value ?? fallback;
    } catch {
      return fallback;
    }
  }, [activeTemplate, allTemplates, allTemplateTypes, templateColumns]);

  const { rowData, allChildRows } = useMemo(() => {
    const rows: PicoRow[] = [];
    const allChildRows: PicoRow[] = []; // every subgroup row, regardless of expand state

    const addStudyRows = (
      id: string,
      studyName: string,
      authors: string,
      countries: string,
      studyId: string,
      status: string,
      subgroups: { population: string; intervention: string; comparator: string; outcome: string; fields?: Record<string, string | FieldValue> }[],
      year?: string,
      sourceOrganisation?: string,
      filename?: string,
      errorMessage?: string,
    ) => {
      const hasChildren = subgroups.length > 0;
      const isExpanded = expandedIds.has(id);
      rows.push({ id, studyName, authors, countries, year, sourceOrganisation, filename, studyId, status, errorMessage, isParent: true, isExpanded: hasChildren ? isExpanded : undefined });
      if (hasChildren) {
        subgroups.forEach((sg, i) => {
          let rowFields: Record<string, string | FieldValue>;
          if (sg.fields) {
            rowFields = { ...sg.fields };
          } else {
            const subgroupValues = [sg.population, sg.intervention, sg.comparator, sg.outcome];
            rowFields = {};
            templateColumns.forEach((col, idx) => {
              rowFields[col.name] = { value: subgroupValues[idx] ?? "", status: "pending" };
            });
          }
          const childRow: PicoRow = { id: `${id}-${i}`, studyName: "", fields: rowFields, isParent: false, parentId: id, span: i === 0 ? subgroups.length : 1 };
          allChildRows.push(childRow);
          if (isExpanded) rows.push(childRow);
        });
      }
    };

    if (gridDocuments.length > 0) {
      // One parent row per document; child rows accumulate across all runs
      gridDocuments.forEach((doc) => {
        const docExtractions = localExtractions.filter((e) => e.studyId === doc.id);
        const allSubgroups = docExtractions.flatMap((e) => e.subgroups);
        const hasData = allSubgroups.length > 0;
        const sentinelEntry = docExtractions.find(
          (e) => (e.status === "Queued" || e.status === "Processing") && e.subgroups.length === 0
        );
        const derivedStatus = sentinelEntry
          ? sentinelEntry.status
          : docExtractions.some((e) => e.status === "Error")
            ? "Error"
            : hasData
              ? "Processed"
              : docExtractions.length > 0
                ? "Processed"
                : "Pending";
        const errorMessage = docExtractions.find((e) => e.status === "Error")?.errorMessage;
        addStudyRows(doc.id, doc.title, doc.author, doc.country, doc.id, derivedStatus, allSubgroups, doc.year, doc.sourceOrganisation, doc.filename, errorMessage);
      });
    } else if (!selectFromEvidenceBase) {
      // Group by studyId so each study shows as one parent with all accumulated subgroups.
      // (With Select from Evidence Base on, no selected documents means an empty grid.)
      const groups = new Map<string, { first: PicoExtraction; subgroups: PicoExtraction["subgroups"] }>();
      localExtractions.forEach((e) => {
        const key = e.studyId || e.id;
        if (!groups.has(key)) groups.set(key, { first: e, subgroups: [] });
        groups.get(key)!.subgroups.push(...e.subgroups);
      });
      groups.forEach(({ first, subgroups }) => {
        addStudyRows(first.studyId || first.id, first.studyName, first.authors, first.countries.join(", "), first.studyId, first.status, subgroups, first.year, first.sourceOrganisation, undefined, first.errorMessage);
      });
    }

    return { rowData: rows, allChildRows };
  }, [gridDocuments, selectFromEvidenceBase, localExtractions, expandedIds, templateColumns]);

  // Plain-text summary of extraction completeness for the current template — fed
  // into the chat assistant's system prompt, independent of expand/collapse state.
  const extractionStateNote = useMemo(() => {
    const documentStatusCounts: Record<string, number> = {};
    let documentCount = 0;
    for (const row of rowData) {
      if (row.isParent) {
        documentCount++;
        const status = row.status ?? "Pending";
        documentStatusCounts[status] = (documentStatusCounts[status] ?? 0) + 1;
      }
    }
    if (documentCount === 0) return null;

    const missingCounts = new Map<string, number>();
    const fieldRowCounts = new Map<string, number>();
    for (const row of allChildRows) {
      if (!row.fields) continue;
      for (const [name, value] of Object.entries(row.fields)) {
        fieldRowCounts.set(name, (fieldRowCounts.get(name) ?? 0) + 1);
        if (!fieldValue(value)) missingCounts.set(name, (missingCounts.get(name) ?? 0) + 1);
      }
    }
    const missingFields = Array.from(missingCounts.entries())
      .map(([name, missing]) => ({ name, missing, total: fieldRowCounts.get(name) ?? 0 }))
      .sort((a, b) => b.missing - a.missing)
      .slice(0, 5);

    const statusStr = Object.entries(documentStatusCounts)
      .filter(([, count]) => count > 0)
      .map(([status, count]) => `${count} ${status}`)
      .join(", ");
    let note = `Extraction (${activeTemplate?.template.name ?? "active template"}): ${documentCount} documents` +
      (statusStr ? ` (${statusStr})` : "") + ".";
    if (missingFields.length > 0) {
      note += ` Fields with missing data: ${missingFields.map((f) => `${f.name} (${f.missing}/${f.total} rows)`).join(", ")}.`;
    }

    return note;
  }, [rowData, allChildRows, activeTemplate]);

  // @-mention provider: resolved fresh at send time (see useAssistantThread in
  // @lumanity/ui), so content is always current — never frozen at mention time.
  const mentionProvider = useMemo<MentionProvider>(() => ({
    search: async (query) => {
      const q = query.toLowerCase();
      return documents
        .filter((d) => (d.title || d.filename || "").toLowerCase().includes(q))
        .slice(0, 20)
        .map((d) => ({ id: d.id, label: d.title || d.filename || d.id, description: d.filename }));
    },
    resolveContent: (item) => {
      const parent = rowData.find((r) => r.isParent && r.id === item.id);
      if (!parent) {
        const doc = documents.find((d) => d.id === item.id);
        return `${doc?.title || doc?.filename || item.label} — no data in the current template yet`;
      }
      const lines = [`${parent.studyName || item.label} — Status: ${parent.status ?? "Pending"}`];
      if (parent.errorMessage) lines.push(`Error: ${parent.errorMessage}`);
      allChildRows
        .filter((r) => r.parentId === item.id)
        .forEach((child, i) => {
          if (!child.fields) return;
          const fieldStrs = Object.entries(child.fields).map(([name, v]) => {
            const value = fieldValue(v) || "(empty)";
            const status = typeof v === "object" && v ? (v as FieldValue).status : undefined;
            return status ? `${name}: ${value} [${status}]` : `${name}: ${value}`;
          });
          if (fieldStrs.length) lines.push(`Subgroup ${i + 1}: ${fieldStrs.join("; ")}`);
        });
      return lines.join("\n");
    },
  }), [documents, rowData, allChildRows]);

  useEffect(() => {
    setMentionProvider(mentionProvider);
    return () => setMentionProvider(null);
  }, [mentionProvider, setMentionProvider]);

  useEffect(() => {
    setStepStateNote(extractionStateNote);
    return () => setStepStateNote(null);
  }, [extractionStateNote, setStepStateNote]);

  const columnDefs = useMemo<ColDef<PicoRow>[]>(() => {
    const studyNameCol: ColDef<PicoRow> = {
      headerName: "Study Name",
      headerTooltip: "Study Name",
      field: "studyName",
      width: 300,
      minWidth: 300,
      colSpan: (params) => (!params.data?.parentId ? templateColumns.length + 1 : 1),
      autoHeight: true,
      wrapText: true,
      cellStyle: (params) => ({
        paddingTop: ".5rem",
        paddingBottom: ".5rem",
        ...(!params.data?.studyName ? {
          // background: "#f7f7f7",
          borderTop: "1px solid #f7f7f7",
          borderBottom: "1px solid #f7f7f7",
        } : {}),
      }),
      cellRenderer: (params: ICellRendererParams<PicoRow> & { context: GridContext }) => {
        const row = params.data!;
        const isExtractingThis = params.context.extractingDocumentId === row.studyId;
        const isAnyExtracting = params.context.isExtracting || params.context.extractingDocumentId !== null;
        const docStatus = row.studyId ? params.context.documentStatuses[row.studyId] : undefined;
        const queuePosition = docStatus?.queue_position;

        const actionNode = row.isParent ? (
          // Right-aligned column: remove (X) on top, then Reprocess / progress, then queue position.
          <div className="flex flex-col items-end gap-1">
            {params.context.selectFromEvidenceBase && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  params.context.onRemoveDocument(row.studyId!);
                }}
                disabled={isAnyExtracting}
                aria-label={`Remove ${row.studyName} from extraction`}
                title={isAnyExtracting ? "Wait for current extraction to complete" : "Remove from extraction (stays in the Evidence Base)"}
                data-testid="remove-from-extraction-btn"
                className={`flex items-center justify-center w-6 h-6 rounded transition-colors ${isAnyExtracting
                  ? "bg-muted text-gray-400 cursor-not-allowed opacity-50"
                  : "bg-destructive/10 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  }`}
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {isExtractingThis ? (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Processing...</span>
              </div>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  params.context.onExtractDocument(row.studyId!);
                }}
                disabled={isAnyExtracting}
                className={`items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${row.status === "Error" || row.status === "Processed" || (row.status === "Pending" && hasExtracted) ? "flex" : "hidden"} ${isAnyExtracting
                  ? "text-gray-400 cursor-not-allowed opacity-50"
                  : "text-brand-teal hover:bg-brand-teal/10"
                  }`}
                title={isAnyExtracting ? "Wait for current extraction to complete" : "Reprocess this document"}
              >
                <Play className="w-3 h-3" />
                <span>Reprocess</span>
              </button>
            )}
            {queuePosition && row.status === "Queued" && (
              <div className="text-xs text-muted-foreground mt-1">
                Position {queuePosition} of {params.context.queueSize}
              </div>
            )}
          </div>
        ) : undefined;

        return (
          <StudyNameCell
            studyName={row.studyName}
            authors={row.authors}
            countries={row.countries}
            year={row.year}
            sourceOrganisation={row.sourceOrganisation}
            filename={row.filename}
            status={getStatusString(row.status)}
            errorMessage={row.errorMessage}
            isParent={row.isParent}
            isExpanded={row.isExpanded}
            onToggle={() => params.context.toggleExpand(row.id)}
            actionNode={actionNode}
          />
        );
      },
    };

    const dataCols: ColDef<PicoRow>[] = templateColumns.map((col, idx) => ({
      headerName: col.name,
      headerTooltip: col.name,
      valueGetter: (params) => {
        const fieldValue = params.data?.fields?.[col.name];
        if (!fieldValue) return "";
        if (typeof fieldValue === "string") return fieldValue;
        return fieldValue.value ?? "";
      },
      flex: 1,
      minWidth: 150,
      autoHeight: true,
      wrapText: true,
      cellStyle: { lineHeight: "inherit", paddingTop: "0.5rem", paddingBottom: "0.5rem" },
      cellRenderer: ExtractedDataCell,
    }));

    return [studyNameCol, ...dataCols];
  }, [templateColumns, hasExtracted]);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    resizable: true,
    filter: true,
  }), []);

  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [rowData, documentStatuses]);

  // With Select from Evidence Base on, export only what the grid shows — a removed document's
  // results can linger in localExtractions until the view remounts.
  const exportExtractions = useMemo(
    () => (selectFromEvidenceBase ? localExtractions.filter((e) => evidenceSelectionDocIds.has(e.studyId)) : localExtractions),
    [selectFromEvidenceBase, localExtractions, evidenceSelectionDocIds]
  );
  const exportRows = usePicoExportRows(exportExtractions, templateColumns);

  // Newly picked documents arrive in the grid after a refetch; tick them once their rows exist so
  // "Extract Selected Sources" is one click away. The grid is uncontrolled, so this goes through its API.
  const pendingPreselectRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || pendingPreselectRef.current.size === 0) return;
    for (const id of [...pendingPreselectRef.current]) {
      const node = api.getRowNode(id);
      if (node) {
        node.setSelected(true);
        pendingPreselectRef.current.delete(id);
      }
    }
  }, [rowData]);

  const handleEvidenceSelectionSaved = useCallback((addedDocumentIds: string[]) => {
    addedDocumentIds.forEach((id) => pendingPreselectRef.current.add(id));
    // A document removed with its X and then re-selected in the popup shows again.
    setRemovedSelectionDocIds((prev) => {
      if (!addedDocumentIds.some((id) => prev.has(id))) return prev;
      const next = new Set(prev);
      addedDocumentIds.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  // A row's X: deselects the document for this template only — it stays in the Evidence Base and can
  // be re-selected via the popup. Pending picks and errors go at once; extracted data is confirmed first.
  const [confirmRemoveDocId, setConfirmRemoveDocId] = useState<string | null>(null);
  const [isRemovingSelection, setIsRemovingSelection] = useState(false);
  const [removeSelectionError, setRemoveSelectionError] = useState<string | null>(null);

  const removeDocumentFromSelection = useCallback(async (documentId: string) => {
    if (!templateId) return;
    const rows = rowsForDocument(templateDocumentRows, documentId);
    setRemoveSelectionError(null);
    setRemovedSelectionDocIds((prev) => new Set(prev).add(documentId));
    gridRef.current?.api?.getRowNode(documentId)?.setSelected(false);

    setIsRemovingSelection(true);
    const results = await Promise.allSettled(
      rows.map((row) => deleteTemplateDocument.mutateAsync({ projectId, templateId, documentDataId: row.id }))
    );
    setIsRemovingSelection(false);

    if (results.some((r) => r.status === "rejected")) {
      console.error(`Failed to remove document ${documentId} from the extraction selection`, results);
      setRemovedSelectionDocIds((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
      setRemoveSelectionError("Couldn't remove the document from this extraction. Try again.");
      invalidateTemplateDocuments();
      return;
    }
    // Its results would otherwise linger in memory until the view remounts.
    setLocalExtractions((prev) => prev.filter((e) => e.studyId !== documentId));
  }, [templateId, templateDocumentRows, deleteTemplateDocument, projectId, invalidateTemplateDocuments]);

  const handleRemoveDocument = useCallback((documentId: string) => {
    if (removalNeedsConfirmation(templateDocumentRows, documentId)) {
      setConfirmRemoveDocId(documentId);
      return;
    }
    void removeDocumentFromSelection(documentId);
  }, [templateDocumentRows, removeDocumentFromSelection]);

  // Cancel deletes the run's Queued/Processing rows, and with them a selected document's only
  // row — so re-create Pending rows for any selection the cancel dropped, on every tab.
  const snapshotEvidenceSelections = useCallback((): Record<string, string[]> =>
    Object.fromEntries(allActiveTemplates.map((t) => {
      const rows = queryClient.getQueryData<TemplateDocumentData[]>(["projects", projectId, "templates", t.template.id, "documents"]);
      return [t.template.id, [...buildEvidenceSelectionDocIds(rows)]];
    })), [allActiveTemplates, queryClient, projectId]);

  const restoreDroppedEvidenceSelections = useCallback(async (snapshot: Record<string, string[]>) => {
    await Promise.all(Object.entries(snapshot).map(async ([tplId, selectedIds]) => {
      if (selectedIds.length === 0) return;
      const queryKey = ["projects", projectId, "templates", tplId, "documents"];
      try {
        const remaining = await projectsApi.getTemplateDocuments(projectId, tplId, true);
        const dropped = droppedDocIds(selectedIds, remaining);
        await Promise.allSettled(dropped.map((documentId) =>
          projectsApi.createTemplateDocument(projectId, tplId, documentId, null, PENDING_SELECTION_STATUS)
        ));
      } catch (err) {
        console.error(`Failed to restore Select from Evidence Base picks for template ${tplId} after cancel:`, err);
      } finally {
        queryClient.invalidateQueries({ queryKey });
      }
    }));
  }, [projectId, queryClient]);

  // Keeps the "Extract Selected Sources" checkboxes/bar visible (disabled) for the whole run instead
  // of clearing them the moment the batch is enqueued — only once extraction actually finishes.
  useEffect(() => {
    if (shouldClearSelectionNow(pendingSelectionClear, isExtracting)) {
      setPendingSelectionClear(false);
      handleClearSelection();
    }
  }, [pendingSelectionClear, isExtracting, handleClearSelection]);

  const isViewedTabExtracting = isInActiveBatch(effectiveTemplateId) || isSingleExtractTargetTab(effectiveTemplateId);

  // Bug #7167: Start Review button must be disabled whenever extraction is in progress —
  // also while still checking for a resumed run, so proceeding can't race that discovery.
  const isStartReviewDisabled = isBusyOrUnknown || (!Object.values(effectiveStatuses).some(s => s === "done") && !hasExtracted);

  const handleCancelExtraction = async () => {
    cancelRequestedRef.current = true;
    const cancelTemplateId = singleExtractTemplateIdRef.current;
    const selectionSnapshot = selectFromEvidenceBase ? snapshotEvidenceSelections() : null;
    if (pollingIntervalRef.current !== null) {
      globalThis.clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    resetExtractionState();
    setTemplateStatuses({});
    setLocalExtractions((prev) =>
      prev.filter((e) => e.status !== "Queued" && e.status !== "Processing")
    );
    if (activeBatchIdRef.current) {
      try {
        await projectsApi.cancelExtractionBatch(projectId, activeBatchIdRef.current);
      } catch (err) {
        console.error("Failed to cancel extraction batch:", err);
      }
      activeBatchIdRef.current = null;
    } else if (cancelTemplateId) {
      await extractionApi.cancelExtraction(projectId, cancelTemplateId);
    }
    invalidateTemplateDocuments();
    if (selectionSnapshot) await restoreDroppedEvidenceSelections(selectionSnapshot);
  };

  const isSelectFromEvidenceBaseDisabled = !templateId || isBusyOrUnknown || isExtractingSelectedSources;
  const selectFromEvidenceBaseTitle = isBusyOrUnknown || isExtractingSelectedSources ? EVIDENCE_SELECT_BUSY_REASON : undefined;
  const extractionTimeLabel = isExtracting ? extractionTimer.estimatedTimeLabel : null;
  const showSelectionEmptyState = selectFromEvidenceBase && !isDocumentsLoading && gridDocuments.length === 0;

  return (
    <Card className="p-6">
      {/* Step header: title and description on the left, export and Continue on the right */}
      <div className="flex flex-col-reverse md:flex-row md:items-center justify-between gap-4 mb-4" data-testid="extraction-header">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-foreground">{pageTitle}</h2>
          <p className="text-sm text-muted-foreground">Extract and organize data from each study in your evidence base into a hierarchical structure showing population/subpopulations, interventions and comparators</p>
        </div>
        <div className="flex items-center gap-2 shrink-0 self-end md:self-auto">
          <ExcelExportButton
            rows={exportRows}
            sheetName={activeTemplate?.template.name ?? "PICO"}
            fileName={`${toSafeFileName(project.name)}-Data-Extraction.xlsx`}
            disabled={!exportRows.length}
          />
          <Button variant="brand" size="sm" onClick={() => handleStageComplete(activeStage)} data-testid="start-review-btn" disabled={isStartReviewDisabled}>
            {continueLabel} <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Template tabs, with the template settings menu on the right */}
      <div className="flex items-center justify-between gap-4" data-testid="extraction-template-row">
        <TemplateTabStrip
          templates={allActiveTemplates.map(t => t.template)}
          selectedId={effectiveTemplateId ?? ""}
          onSelect={setSelectedProjectTemplateId}
          statuses={effectiveStatuses}
        />
        {/* Non-modal: both items open a Sheet/Dialog. A modal menu sets pointer-events:none on <body>
            while closing; the dialog opening at that moment records it as the "original" value and
            restores it on close, leaving every click on the page blocked. */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            {/* The template's name sits beside the gear so it's clear what the menu acts on. */}
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto shrink-0 gap-2 font-medium"
              disabled={isBusyOrUnknown}
              aria-label={`Template settings${activeTemplate ? `: ${activeTemplate.template.name}` : ""}`}
              title={isBusyOrUnknown ? EVIDENCE_SELECT_BUSY_REASON : "Template settings"}
              data-testid="template-settings-menu"
            >
              {activeTemplate && <span className="max-w-[16rem] truncate">{activeTemplate.template.name}</span>}
              <Settings className="w-5 h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setTemplateOpen(true)} disabled={templateColumns.length === 0}>
              Edit template
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setTemplateSelectOpen(true)}>
              Change templates
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Toolbar: extraction progress on the left, selection source and filters on the right */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-testid="extraction-toolbar">
        <div className="flex flex-wrap items-center gap-2">
          {isViewedTabExtracting && (
            <div className="flex items-center gap-3" data-testid="extraction-progress">
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Extracting {extractProgress.current} of {extractProgress.total}...
              </span>
              {templateId && (
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={!canCancelExtraction}
                  title={canCancelExtraction ? undefined : "Starting extraction — cancel will be available shortly"}
                  onClick={handleCancelExtraction}
                >
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 ml-auto">
          {selectFromEvidenceBase && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setEvidenceSelectOpen(true)}
              disabled={isSelectFromEvidenceBaseDisabled}
              title={selectFromEvidenceBaseTitle}
              data-testid="select-from-evidence-base-btn"
            >
              {SELECT_FROM_EVIDENCE_BASE_TITLE}
            </Button>
          )}
          <ResetFiltersButton hasActiveFilters={hasGridColumnFilters} onReset={handleResetGridFilters} />
        </div>
      </div>

      {/* Also shown for a run resumed after a reload, which has nothing selected, so its timer stays visible. */}
      {(totalSelectedCount > 0 || extractionTimeLabel) && (
        <div
          className="flex items-center justify-between gap-3 mb-4 px-4 py-2 border bg-[#e6eff0] border-[#bad7d7]"
          data-testid="extraction-selection-bar"
        >
          <div className="flex flex-wrap items-center gap-3">
            {totalSelectedCount > 0 && (
              <span className="text-sm font-medium text-foreground">
                {formatSelectionLabel(totalSelectedCount)}
              </span>
            )}
            {extractionTimeLabel && (
              <span className="text-sm text-muted-foreground italic" data-testid="extraction-timer">
                {extractionTimeLabel}
              </span>
            )}
          </div>
          {totalSelectedCount > 0 && (
            <Button
              variant="brand"
              size="sm"
              onClick={handleExtractSelectedSources}
              disabled={isExtractSelectedSourcesDisabled(isBusyOrUnknown, isExtractingSelectedSources, totalSelectedCount)}
              className="gap-1.5"
            >
              {isExtractingSelectedSources ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              Extract ({totalSelectedCount})
            </Button>
          )}
        </div>
      )}

      {selectFromEvidenceBase && removeSelectionError && (
        <Alert variant="destructive" className="mb-4" data-testid="remove-from-extraction-error">
          <AlertDescription>{removeSelectionError}</AlertDescription>
        </Alert>
      )}

      <div className="relative ag-theme-alpine border overflow-hidden" style={{ height: 500 }}>
        <AgGridReact<PicoRow>
          // A fresh grid per template tab: every tab shows the same rows under the same ids, so
          // a shared grid would carry one tab's checked rows into the next. initialState is only
          // read when the grid is created, and restores that tab's own checked rows.
          key={effectiveTemplateId}
          initialState={{ rowSelection: [...viewedTabSelectedIds] }}
          theme="legacy"
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          context={{ toggleExpand, documents: documents ?? [], onViewDocument, onExtractDocument: handleExtractSingleDocument, extractingDocumentId, isExtracting: isBusyOrUnknown || isExtractingSelectedSources, documentStatuses, queueSize, selectFromEvidenceBase, onRemoveDocument: handleRemoveDocument } satisfies GridContext}
          suppressRowTransform={true}
          animateRows={true}
          headerHeight={GRID_HEADER_HEIGHT}
          getRowId={(params) => params.data.id}
          onFilterChanged={onFilterChanged}
          selectionColumnDef={selectionColumnDef}
          rowSelection={rowSelection}
          onSelectionChanged={onGridSelectionChanged}
          // Select from Evidence Base draws its own empty state (below), with a button in it.
          suppressNoRowsOverlay={selectFromEvidenceBase}
        />
        {showSelectionEmptyState && (
          <div
            className="absolute inset-x-0 bottom-0 flex flex-col items-center justify-center gap-3 px-6 text-center"
            style={{ top: GRID_HEADER_HEIGHT }}
            data-testid="no-documents-selected"
          >
            <p className="text-sm font-medium text-foreground">{NO_DOCUMENTS_SELECTED_TITLE}</p>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setEvidenceSelectOpen(true)}
              disabled={isSelectFromEvidenceBaseDisabled}
              title={selectFromEvidenceBaseTitle}
            >
              {SELECT_FROM_EVIDENCE_BASE_TITLE}
            </Button>
            <p className="text-sm text-muted-foreground max-w-md">{SELECTION_EMPTY_STATE_HINT}</p>
          </div>
        )}
      </div>

      {selectFromEvidenceBase && (
        <SelectFromEvidenceBaseDialog
          open={evidenceSelectOpen}
          onOpenChange={setEvidenceSelectOpen}
          documents={documents ?? []}
          templateDocuments={templateDocumentRows ?? []}
          projectId={projectId}
          templateId={templateId}
          saveDisabledReason={isBusyOrUnknown || isExtractingSelectedSources ? EVIDENCE_SELECT_BUSY_REASON : undefined}
          onGoToEvidenceBase={() => {
            setEvidenceSelectOpen(false);
            goToStep(EVIDENCE_BASE_STEP_KEY);
          }}
          onSaved={handleEvidenceSelectionSaved}
        />
      )}

      {selectFromEvidenceBase && (
        <DeleteConfirmationDialog
          open={confirmRemoveDocId !== null}
          title="Remove from extraction"
          description="This removes the document from this template's extraction and permanently deletes its extracted data. The document stays in the Evidence Base and can be selected again."
          itemName={documents?.find((d) => d.id === confirmRemoveDocId)?.title}
          isDeleting={isRemovingSelection}
          onConfirm={() => {
            const documentId = confirmRemoveDocId;
            setConfirmRemoveDocId(null);
            if (documentId) void removeDocumentFromSelection(documentId);
          }}
          onCancel={() => setConfirmRemoveDocId(null)}
        />
      )}

      {/* Template Selection Dialog */}
      <TemplateSelectDialog
        projectType={project?.projectType ?? null}
        open={(!isTemplateLoading && allActiveTemplates.length === 0) || templateSelectOpen}
        currentTemplateIds={allActiveTemplates.map(t => t.template.source_template_id)}
        formatType="Extraction"
        onClose={() => setTemplateSelectOpen(false)}
      />

      {/* Template Sheet */}
      <Sheet open={templateOpen} onOpenChange={setTemplateOpen}>
        <SheetContent className="flex flex-col w-full md:w-[40svw] sm:max-w-none p-0 gap-0 bg-gray-100">
          <SheetHeader className="px-4 pt-6 pb-4 border-b shrink-0 bg-white">
            <SheetTitle className="text-lg font-semibold">Template</SheetTitle>
          </SheetHeader>
          {activeTemplate && (
            <ProjectTemplateForm
              onClose={() => setTemplateOpen(false)}
              activeTemplate={activeTemplate}
              projectId={projectId}
              projectType={project?.projectType}
              allTemplates={allActiveTemplates.map(t => ({ id: t.template.id, name: t.template.name, status: effectiveStatuses[t.template.id] }))}
              selectedTemplateId={effectiveTemplateId ?? ""}
              onSelectTemplate={setSelectedProjectTemplateId}
            />
          )}
        </SheetContent>
      </Sheet>

    </Card>
  );
};

export default PicoExtractionView;
